import axios, { CancelTokenSource } from 'axios'

import { RrRequestParams } from '@/commons/types'
import { ErrorUtils } from '@/commons/utils/error'
import { ApplicationEntity } from '@/store/entities'
import { PersonUtils } from '@/commons/utils/person'
import { ENVIRONMENT } from '@/commons/configuration'
import { ApplicationUtils } from '@/commons/utils/application'
import { ApplicationType } from '@/commons/constants/application'

import {
    RrService,
    applicantStore,
    BiometryService,
    classifiersStore,
    ApplicationService
} from '@/store'
import { ClassifiersUtils } from '@/commons/utils/classifiers'

export class ApplicantService {
    static async fetchApplicantInfo (personCode: string, applicationType: ApplicationType): Promise<void> {
        if (applicantStore.isValidApplicant) {
            return
        }
        try {
            applicantStore.requestCancelToken = axios.CancelToken.source()
            applicantStore.application = new ApplicationEntity()

            await Promise.all([
                this.fetchApplicantBiometry(personCode, applicantStore.requestCancelToken, applicationType),
                this.fetchApplicantRrInfo(personCode, applicationType, applicantStore.requestCancelToken),
                ApplicationService.fetchOngoingApplication(
                    personCode, applicantStore.requestCancelToken, applicationType
                )
            ])

            applicantStore.requestCancelToken = null

            // RR info should not overwrite saved data birthCountry
            if (applicantStore.applicationApplicant.birthCountry) {
                return
            }

            const rrBirthCountryIso = applicantStore.rrPersonBirthCountry.replace(
                'BIRTHCOUNTRY_',
                ''
            )
            const birthCountryFromClassifier = ClassifiersUtils.findLikeClassifier(
                classifiersStore.placeOfBirths,
                new RegExp(`.*/ ${rrBirthCountryIso}$`, 'gi')
            )

            if (birthCountryFromClassifier) {
                applicantStore.applicationApplicant.birthCountry = birthCountryFromClassifier.code
            }
        } catch (e) {
            ErrorUtils.requestErrorHandler(e)
        }
    }

    static async fetchApplicantRrInfo (
        personCode: string, applicationType: ApplicationType, requestCancelToken: CancelTokenSource
    ): Promise<void> {
        try {
            const requestParams: RrRequestParams = {
                personCode: personCode,
                type: applicationType
            }
            const response = await RrService.fetchRRInfo(ENVIRONMENT.rrApplicantUrl, requestCancelToken, requestParams)
            applicantStore.rrInfo = response.data
        } catch (e) {
            throw ErrorUtils.resolveRequestErrorEntity(e)
        }
    }

    static async fetchApplicantBiometry (
        personCode: string,
        requestCancelToken: CancelTokenSource,
        applicationType: ApplicationType): Promise<void> {
        try {
            let fingerprintsResponse = null
            let biometryResponse = null
            if (ApplicationUtils.isFingerprintsRequired(applicationType, PersonUtils.calculateAge(personCode))) {
                [fingerprintsResponse, biometryResponse] = await Promise.all([
                    BiometryService.fetchFingerprints(personCode, requestCancelToken, applicationType),
                    BiometryService.fetchBiometry(personCode, requestCancelToken)
                ])
            } else {
                fingerprintsResponse = { data: { sessionTime: null } }
                biometryResponse = await BiometryService.fetchBiometry(personCode, requestCancelToken)
            }
            applicantStore.fingerprintsCaptured = fingerprintsResponse.data?.sessionTime || null
            applicantStore.biometry = biometryResponse.data
        } catch (e) {
            throw ErrorUtils.resolveRequestErrorEntity(e)
        }
    }

    static resetApplicantStore (): void {
        this.cancelRequests()
        applicantStore.clear()
    }

    static cancelRequests (): void {
        if (applicantStore.requestCancelToken) {
            applicantStore.requestCancelToken.cancel()
        }
    }
}
